import { BRANCH_LEN, IllegalActionError, SAFE, TOKENS_PER_PLAYER, YARD, branchCell, homeCell, isBranch, isBranchForColor, isHome } from "./types.js";
export function isPlayableSeat(state, color) {
    const player = state.players.find((candidate) => candidate.seat === color);
    return Boolean(player && (player.status === "human" || player.status === "bot") && !state.finished.includes(color));
}
/** Rejects malformed or impossible states before an authoritative transition. */
export function assertValidState(state) {
    const playerColors = new Set(state.players.map((player) => player.seat));
    if (state.players.length < 1 || state.players.length > 4 || playerColors.size !== state.players.length)
        invalid("invalid_players", "The match has an invalid player roster");
    if (!isPlayableSeat(state, state.turn) && state.phase !== "over")
        invalid("invalid_turn", "The current turn does not belong to a playable player");
    if (state.phase === "roll" && (state.die !== null || (state.dice && state.dice.length > 0)))
        invalid("invalid_phase", "A roll phase cannot retain an active die or dice");
    if (state.phase === "move" && (!state.die && !(state.dice && state.dice.length > 0)))
        invalid("invalid_phase", "A move phase requires a valid die");
    if (state.finished.some((color, index) => state.finished.indexOf(color) !== index || !playerColors.has(color)))
        invalid("invalid_finishers", "Finished players are invalid");
    const tokenIds = new Set();
    for (const player of state.players) {
        const tokens = state.tokens.filter((token) => token.color === player.seat);
        if (tokens.length !== TOKENS_PER_PLAYER)
            invalid("invalid_tokens", "Each player must own exactly four tokens");
        for (const token of tokens) {
            if (tokenIds.has(token.id))
                invalid("invalid_tokens", "Token ids must be unique");
            tokenIds.add(token.id);
            if (token.cell === YARD || (token.cell >= 0 && token.cell < 52))
                continue;
            if (isHome(token.cell) && token.cell === homeCell(token.color))
                continue;
            if (!isBranchForColor(token.cell, token.color) || token.cell >= branchCell(token.color, BRANCH_LEN))
                invalid("invalid_cell", "A token occupies an invalid path cell");
        }
    }
    if (state.tokens.some((token) => !playerColors.has(token.color)))
        invalid("invalid_tokens", "A token has no player");
    if (state.finished.some((color) => state.tokens.filter((token) => token.color === color).some((token) => !isHome(token.cell))))
        invalid("invalid_finishers", "Finished players must have every token Home");
    for (const cell of new Set(state.tokens.filter((token) => token.cell !== YARD && !isBranch(token.cell) && !isHome(token.cell)).map((token) => token.cell))) {
        const stack = state.tokens.filter((token) => token.cell === cell);
        if (stack.length > 2 && !SAFE.has(cell))
            invalid("invalid_stack", "A regular board cell cannot contain more than two tokens");
        if (stack.length > 1 && !SAFE.has(cell) && !areFriendly(state, stack[0]?.color, stack[1]?.color))
            invalid("invalid_stack", "Opposing tokens cannot share a capturable cell");
        if (!state.rules.safeCellsShareable && SAFE.has(cell) && new Set(stack.map((token) => token.color)).size > 1)
            invalid("invalid_safe_stack", "Different colours cannot share a safe cell in this rule set");
    }
}
export function areFriendly(state, first, second) {
    if (first === undefined || second === undefined)
        return false;
    if (first === second)
        return true;
    const a = state.players.find((player) => player.seat === first);
    const b = state.players.find((player) => player.seat === second);
    return Boolean(state.rules.teams && a && b && a.team !== null && a.team === b.team);
}
function invalid(code, message) { throw new IllegalActionError(code, message); }
